import { FC } from 'react';
import { Stack, Typography } from '@mui/material';

import { FormattedEndAt } from './FormattedEndAt';

import { LocalizationTypography } from '~components/organisms/localization';
import { useLanguageContext } from '~providers';
import { formatCurrency, formatPricing } from '~utils';

interface PricingTextProps {
  price: number;
  unit: string;
  unitQuantity: number;
  currencySymbol: string;
  allowBatterySwap: boolean;
  maxSwaps: number;
  endAt?: string;
  maxChargeAmount: number;
}

export const PricingText: FC<PricingTextProps> = ({
  price,
  unit,
  unitQuantity,
  currencySymbol,
  allowBatterySwap,
  maxSwaps,
  endAt,
  maxChargeAmount,
}) => {
  const { lt } = useLanguageContext();

  const pricingDisplay = formatPricing(unitQuantity, unit, lt);

  return (
    <Stack gap={1}>
      <Stack direction="row" gap={0.5} alignItems="center">
        <Typography variant="Poppins/Subheading-xl" color="#111928">
          {formatCurrency(price, currencySymbol, 2)}
        </Typography>
        {!allowBatterySwap && (
          <Typography variant="Poppins/Subheading-sm" color="#111928">
            {pricingDisplay}
          </Typography>
        )}
        {!allowBatterySwap && !!maxChargeAmount && (
          <LocalizationTypography
            variant="Poppins/Subheading-xl"
            color="#111928"
            textId="app.rentalPage.newStepThree.rentCharger.maxChargeAmount"
            defaultText="• Max {{value}}"
            textValues={{
              value: formatCurrency(maxChargeAmount, currencySymbol),
            }}
          />
        )}
      </Stack>

      {allowBatterySwap &&
        (maxSwaps === -1 ? (
          <LocalizationTypography
            textId="app.rentalPage.newStepThree.rentCharger.unlimitedMaxSwaps"
            defaultText="includes unlimited charger swaps"
            variant="Poppins/Body-sm"
            color="#111928"
          />
        ) : (
          <LocalizationTypography
            textId="app.rentalPage.newStepThree.rentCharger.maxSwaps"
            defaultText="includes {{maxSwaps}} charger swaps"
            textValues={{
              maxSwaps,
            }}
            variant="Poppins/Body-sm"
            color="#111928"
          />
        ))}

      {/* The caller decides whether there is a return-by date worth showing:
          subscriptions have always had one, and day-unit passes now do too. */}
      {endAt && (
        <Stack direction="row" gap={0.5} alignItems="center">
          <LocalizationTypography
            textId="app.rentalPage.newStepThree.rentCharger.validUntil"
            defaultText="Valid until"
            variant="Poppins/Body-sm"
            color="#111928"
          />
          <Typography variant="Poppins/Body-sm" color="#111928">
            <FormattedEndAt endAt={endAt} />
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};
